import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../auth/AuthContext";
import { fetchProfile } from "../api/user";
import { fetchConditions, reportBaseDepth } from "../api/conditions";
import { fetchResortStatuses, reportResortStatus, clearResortStatus } from "../api/resortStatus";
import { RESORTS } from "../data/resorts";
import { PASS_TYPE_LABELS } from "../constants/passTypes";
import { WIND_HOLD_LABELS } from "../utils/snowScore";
import { formatSnowfall } from "../utils/units";
import { ResortStatusBanner } from "./ResortStatusBanner";

function ReportBaseDepthForm({ resortId, onDone }) {
  const [value, setValue] = useState("");
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (inches) => reportBaseDepth(resortId, inches),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["conditionsBoard"] });
      onDone?.();
    },
  });

  return (
    <form
      className="report-base-depth-form"
      onSubmit={(e) => {
        e.preventDefault();
        const inches = Number(value);
        if (Number.isFinite(inches) && inches >= 0) mutation.mutate(inches);
      }}
    >
      <input
        type="number"
        min="0"
        max="300"
        placeholder="Base depth (in)"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        required
      />
      <button type="submit" className="btn-accent" disabled={mutation.isPending}>
        {mutation.isPending ? "Saving…" : "Report"}
      </button>
      {mutation.isError && <p className="badge-warn">Couldn't save — try again.</p>}
    </form>
  );
}

function scoreNumClass(score) {
  if (score >= 60) return "ok";
  if (score >= 40) return "warn";
  return "bad";
}

function windPillClass(risk) {
  if (risk === "clear") return "clear";
  if (risk === "breezy") return "breezy";
  return "hold"; // upper_mtn, summit, high
}

function ResortCard({ resort, conditions, onPass, status, canReport, onReportStatus, onClearStatus }) {
  const [reporting, setReporting] = useState(false);
  const hasData = conditions && conditions.status !== "no_data";

  return (
    <div className="glass resort-card-wrap">
      <div className="resort-card">
        <div className="resort-main">
          <div className="resort-name">
            {resort.name}
            {onPass && <span className="badge-ok on-pass-badge">On your pass</span>}
          </div>
          {hasData ? (
            <>
              <div className="resort-meta">
                {resort.route.toUpperCase()}
                <span className="dot" />
                {formatSnowfall(conditions.newSnowInches ?? 0)} new
                <span className="dot" />
                {conditions.baseDepthInches != null ? `${formatSnowfall(conditions.baseDepthInches)} base` : "no base report"}
              </div>
              <span className={`wind-pill ${windPillClass(conditions.windHoldRisk)}`}>
                {WIND_HOLD_LABELS[conditions.windHoldRisk] ?? "—"}
              </span>
            </>
          ) : (
            <p className="sec-dek">No conditions fetched yet.</p>
          )}
        </div>
        {hasData && (
          <div className="score-badge">
            <div className={`score-num ${scoreNumClass(conditions.score)}`}>{conditions.score}</div>
            <div className="score-lbl">{conditions.scoreLabel}</div>
          </div>
        )}
      </div>
      <ResortStatusBanner status={status} canReport={canReport} onReport={onReportStatus} onClear={onClearStatus} />
      <div className="resort-report-row">
        {reporting ? (
          <ReportBaseDepthForm resortId={resort.resortId} onDone={() => setReporting(false)} />
        ) : (
          <button className="pill" onClick={() => setReporting(true)}>
            Report base depth
          </button>
        )}
      </div>
    </div>
  );
}

export function ConditionsTab() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ["conditionsBoard"],
    queryFn: () => Promise.all(RESORTS.map((r) => fetchConditions(r.resortId))),
    refetchInterval: 5 * 60 * 1000,
  });
  const { data: profile } = useQuery({
    queryKey: ["profile", user?.userId],
    queryFn: () => fetchProfile(user.userId),
    enabled: !!user?.userId,
  });
  const { data: statuses } = useQuery({
    queryKey: ["resortStatuses"],
    queryFn: fetchResortStatuses,
    refetchInterval: 2 * 60 * 1000,
  });

  const invalidateStatuses = () => queryClient.invalidateQueries({ queryKey: ["resortStatuses"] });
  const reportStatusMutation = useMutation({
    mutationFn: ({ resortId, flagType, message }) => reportResortStatus(resortId, flagType, message),
    onSuccess: invalidateStatuses,
  });
  const clearStatusMutation = useMutation({
    mutationFn: (resortId) => clearResortStatus(resortId),
    onSuccess: invalidateStatuses,
  });

  if (isLoading) return <p>Loading conditions…</p>;
  if (error) return <p className="badge-warn">Couldn't load conditions.</p>;

  const isDemo = data?.some((c) => c?.isDemoData);
  const passType = profile?.passType && profile.passType !== "none" ? profile.passType : null;
  const isOnPass = (resort) => !!passType && resort.passes.includes(passType);
  const statusByResort = Object.fromEntries((statuses || []).map((s) => [s.resortId, s]));

  const rows = RESORTS.map((resort, i) => ({ resort, conditions: data[i] }));
  const onPassRows = passType ? rows.filter((r) => isOnPass(r.resort)) : [];
  const otherRows = passType ? rows.filter((r) => !isOnPass(r.resort)) : rows;

  const statusProps = (resortId) => ({
    status: statusByResort[resortId] || null,
    canReport: !!user,
    onReportStatus: (flagType, message) => reportStatusMutation.mutateAsync({ resortId, flagType, message }),
    onClearStatus: () => clearStatusMutation.mutateAsync(resortId),
  });

  return (
    <section>
      <h2>Conditions</h2>
      <p className="sec-dek">
        Score = new snow (forecast) + base depth (community-reported) − wind-hold risk (forecast).
      </p>
      {isDemo && (
        <span className="badge-info demo-badge">
          Demo data — the season hasn't started. Real forecasts take over automatically once it does.
        </span>
      )}
      {onPassRows.length > 0 && (
        <>
          <div className="sec-hd">
            <span className="sec-title">On your {PASS_TYPE_LABELS[passType]}</span>
          </div>
          <div className="resort-grid">
            {onPassRows.map(({ resort, conditions }) => (
              <ResortCard
                key={resort.resortId}
                resort={resort}
                conditions={conditions}
                onPass
                {...statusProps(resort.resortId)}
              />
            ))}
          </div>
          <div className="sec-hd">
            <span className="sec-title">Other resorts</span>
          </div>
        </>
      )}
      <div className="resort-grid">
        {otherRows.map(({ resort, conditions }) => (
          <ResortCard key={resort.resortId} resort={resort} conditions={conditions} {...statusProps(resort.resortId)} />
        ))}
      </div>
    </section>
  );
}
